import type { Permission } from "./access";
import type { Appointment, AppointmentStatusName } from "./index";

/**
 * **The combined calendar** (STORY-195, CF-D1–CF-D17): several locations of one
 * clinic drawn in one calendar. Every read is clinic-wide and every write stays
 * with the booking's own location (CF-D15).
 *
 * In this file "location" is a branch, as everywhere in the product; the
 * prototype and Emile say "clinic A, clinic B" for the same thing.
 */

/** The most locations one combined read will answer for (brief: cap at 12). */
export const CALENDAR_LOCATION_LIMIT = 12;

/** How many badge hues there are; a location's hue is its creation index mod this. */
export const CALENDAR_HUES = 10;

/**
 * **A location's code, worked out from its name** (CF-D2): the first three
 * letters, upper case — `Achrafieh` → `ACH`. Accents are folded (`Éden` → `EDE`)
 * and anything that is not a letter is skipped; a name in Arabic script keeps
 * its first three letters as written (they have no case).
 *
 * **When two codes clash, the location created later takes one more letter at a
 * time** until its letters differ from every earlier location's at that length:
 * `Achrafieh`, `Achrafaye` → `ACH`, `ACHRAFA`. The earlier location never
 * changes, so a code people have learnt does not move when a branch is added.
 *
 * Two names whose letters are identical all the way (`Hamra`, `Hamra!`) cannot
 * be told apart by letters, so the later one is numbered: `HAM`, `HAM2`.
 *
 * @param namesInCreationOrder every location of the clinic, oldest first —
 *   closed ones included, so a code does not change when a branch closes.
 * @returns one code per name, in the same order.
 */
export function branchCodes(namesInCreationOrder: readonly string[]): string[] {
  const letters = namesInCreationOrder.map(lettersOf);
  const codes: string[] = [];

  letters.forEach((mine, index) => {
    const base = mine.slice(0, 3);
    // Only earlier locations that start the same way can clash with this one.
    const rivals = letters.slice(0, index).filter((theirs) => theirs.slice(0, 3) === base);

    let length = 3;
    while (
      length < mine.length &&
      rivals.some((theirs) => theirs.slice(0, length) === mine.slice(0, length))
    ) {
      length += 1;
    }

    // Letters ran out before they differed: keep the three-letter code and
    // number it, the one way left to tell them apart.
    const exhausted = rivals.some((theirs) => theirs.slice(0, length) === mine.slice(0, length));
    let code = exhausted ? base : mine.slice(0, length);
    if (codes.includes(code)) {
      let n = 2;
      while (codes.includes(`${code}${String(n)}`)) n += 1;
      code = `${code}${String(n)}`;
    }
    codes.push(code === "" ? `L${String(index + 1)}` : code);
  });

  return codes;
}

/** A name's letters, accents folded, upper case. Digits stand in only when there are no letters. */
function lettersOf(name: string): string {
  const folded = name.normalize("NFD").replace(/\p{M}/gu, "");
  const letters = [...folded].filter((ch) => /\p{L}/u.test(ch)).join("");
  const kept = letters === "" ? [...folded].filter((ch) => /\p{N}/u.test(ch)).join("") : letters;
  return kept.toLocaleUpperCase("en");
}

/**
 * One location in a combined answer, and what the caller may do there.
 *
 * `permissions` is the caller's access **at this location** (CF-D9, R-D13), so
 * the side panel can offer a booking's actions as that location allows them
 * rather than as the active location does.
 */
export interface CalendarLocation {
  id: string;
  name: string;
  /** `ACH` — see `branchCodes`. */
  code: string;
  /** 0–9: the badge hue, the location's index in creation order mod 10. */
  hue: number;
  timezone: string;
  currency: string;
  /** Whether the caller sees every booking here (holds `calendar.view` here), or only their own. */
  seesAll: boolean;
  permissions: Permission[];
}

/** A location's day: whether it opens, and when, on its own clock. */
export interface CalendarLocationDay {
  /** Local date in the location's own zone. */
  date: string;
  isOpen: boolean;
  /** `08:00`, or null when shut. Opening hours are display only (H2). */
  opensAt: string | null;
  closesAt: string | null;
}

export interface CalendarChair {
  id: string;
  name: string;
  sortOrder: number;
}

/** A clinician working at a location on the first day asked for. */
export interface CalendarProvider {
  userId: string;
  name: string;
  /** `DENTIST` or `HYGIENIST` — only people who may be a provider are listed. */
  role: string;
  /** Rostered and working that day (not on leave, training or off). */
  isAvailable: boolean;
}

/**
 * Everything one location contributes to a combined day or week.
 * `appointments` are the same shape `GET /appointments` returns.
 */
export interface CombinedLocation {
  branch: CalendarLocation;
  days: CalendarLocationDay[];
  /** Active chairs, in the location's own order. */
  chairs: CalendarChair[];
  providers: CalendarProvider[];
  appointments: Appointment[];
}

/** `GET /calendar/combined?from=&days=1|7&branchIds=`. */
export interface CombinedCalendar {
  from: string;
  days: number;
  locations: CombinedLocation[];
}

/** One location's day in the combined month. */
export interface CombinedMonthDay {
  date: string;
  isOpen: boolean;
  count: number;
  minutesBooked: number;
}

export interface CombinedMonthLocation {
  branch: CalendarLocation;
  /** Chair minutes on an open day: active chairs × that weekday's open minutes, by weekday 0 (Sun)–6. */
  capacityByWeekday: number[];
  days: CombinedMonthDay[];
}

/** `GET /calendar/combined/month?month=&branchIds=` plus the filters below. */
export interface CombinedMonth {
  month: string;
  /** Empty cells before the 1st, Sunday first — as `Month`. */
  leadingBlanks: number;
  locations: CombinedMonthLocation[];
}

/**
 * The status groups the filter offers (CF-D16). **Checked in counts as
 * confirmed** (CF-D4). Cancelled and missed bookings are in no group: neither
 * is work anybody has to do, so neither is counted or filtered.
 */
export type CalendarStatusGroup = "NOT_CONFIRMED" | "CONFIRMED" | "IN_CHAIR" | "COMPLETED";

export const CALENDAR_STATUS_GROUPS: readonly CalendarStatusGroup[] = [
  "NOT_CONFIRMED",
  "CONFIRMED",
  "IN_CHAIR",
  "COMPLETED",
];

export const CALENDAR_STATUS_GROUP_LABELS: Record<CalendarStatusGroup, string> = {
  NOT_CONFIRMED: "Not confirmed",
  CONFIRMED: "Confirmed",
  IN_CHAIR: "In chair",
  COMPLETED: "Completed",
};

/** Which group a status falls in, or null for cancelled and no-show. */
export function statusGroupOf(status: AppointmentStatusName): CalendarStatusGroup | null {
  switch (status) {
    case "NOT_CHECKED_IN":
      return "NOT_CONFIRMED";
    case "CONFIRMED":
    case "CHECKED_IN":
      return "CONFIRMED";
    case "IN_CHAIR":
      return "IN_CHAIR";
    case "COMPLETED":
      return "COMPLETED";
    default:
      return null;
  }
}

/** The filled circle (CF-D4): anything checked in, in the chair or finished counts as confirmed. */
export function isConfirmedStatus(status: AppointmentStatusName): boolean {
  const group = statusGroupOf(status);
  return group !== null && group !== "NOT_CONFIRMED";
}

/**
 * The appointment-type key a booking files under: its procedure category's id,
 * `FIRST_VISIT` for a first visit (a tag, not a colour — CF-D4, and it wins),
 * or `NONE` for a booking with no category.
 */
export const FIRST_VISIT_TYPE = "FIRST_VISIT";
export const NO_CATEGORY_TYPE = "NONE";

export function appointmentTypeOf(appointment: Appointment): string {
  if (appointment.isFirstVisit) return FIRST_VISIT_TYPE;
  return appointment.procedureCategoryId ?? NO_CATEGORY_TYPE;
}

/**
 * What the filter panel has hidden (CF-D16) — per group, the values that are
 * off; nothing hidden is everything shown. `onlyProviderId` is the doctor's
 * "Only my bookings" (CF-D11).
 */
export interface CalendarFilters {
  branchIds: string[];
  providerIds: string[];
  chairIds: string[];
  types: string[];
  statuses: CalendarStatusGroup[];
  onlyProviderId: string | null;
}

/**
 * Whether a booking survives the filters. **Held chairs and bookings that
 * hold nothing** (cancelled, no-show) are not counted by the filters at all:
 * the first are not appointments and the second are not work.
 */
export function passesFilters(appointment: Appointment, filters: CalendarFilters): boolean {
  if (filters.branchIds.includes(appointment.branchId)) return false;
  if (filters.chairIds.includes(appointment.chairId)) return false;
  if (filters.onlyProviderId !== null && appointment.providerId !== filters.onlyProviderId)
    return false;
  if (appointment.providerId !== null && filters.providerIds.includes(appointment.providerId))
    return false;
  if (appointment.patient === null) return true;
  if (filters.types.includes(appointmentTypeOf(appointment))) return false;
  const group = statusGroupOf(appointment.status);
  if (group !== null && filters.statuses.includes(group)) return false;
  return true;
}

/** A location the picker may offer: one the caller works at, or any with `branches.all`. */
export interface CalendarPickerLocation {
  id: string;
  name: string;
  /** Worked out over every location of the clinic, as the combined read does. */
  code: string;
  hue: number;
  timezone: string;
  /** Today's opening hours on the location's own clock; null when shut today. */
  opensAt: string | null;
  closesAt: string | null;
}

/** `GET/PUT /users/me/calendar-locations` — the remembered selection (CF-D12). */
export interface CalendarSelection {
  /** Empty means "only the active location" — today's single calendar. */
  branchIds: string[];
  /**
   * Every open location the caller may show, in creation order. Returned by
   * GET and by PUT, so the picker needs no second read.
   */
  reachable: CalendarPickerLocation[];
  /** Holds `calendar.across_locations` at the active location (CF-D8). */
  canCombine: boolean;
}
